import React from "react";
import { SlidersHorizontal } from "lucide-react";
import { DatePicker } from "antd";
import CommonSelect from "../../../../core/common/commonSelect";
import { REFERENCE_OPTIONS } from "../constants/callConstants";
import dayjs from "dayjs";

interface LiveCallFiltersProps {
    showFilters: boolean;
    tempFilters: any;
    uniqueValues: any;
    isUsedCall: boolean;
    activeFilterCount: number;
    setTempFilters: React.Dispatch<React.SetStateAction<any>>;
    handleApplyFilters: () => void;
    handleReset: () => void;
}

const LiveCallFilters: React.FC<LiveCallFiltersProps> = ({
    showFilters,
    tempFilters,
    uniqueValues,
    isUsedCall,
    activeFilterCount,
    setTempFilters,
    handleApplyFilters,
    handleReset,
}) => {
    if (!showFilters) return null;

    return (
        <div className="">
            {/* Header */}
            <div className="lc-filter-header">
                <div className="lc-filter-icon">
                    <SlidersHorizontal size={14} />
                </div>
                <h4 className="lc-filter-title">Filter by</h4>
            </div>

            {/* Fields */}
            <div className="lc-field-group">
                <div className="lc-fields-stack">
                    {/* Country */}
                    <div>
                        <label className="lc-label">Country</label>
                        <CommonSelect
                            defaultValue={
                                tempFilters.country
                                    ? { value: tempFilters.country, label: tempFilters.country }
                                    : { value: "", label: "All" }
                            }
                            options={[
                                { value: "", label: "All" },
                                ...uniqueValues.countries.map((v: string) => ({
                                    value: v,
                                    label: v,
                                })),
                            ]}
                            onChange={(val: any) =>
                                setTempFilters((p: any) => ({
                                    ...p,
                                    country: val?.value ?? "",
                                }))
                            }
                        />
                    </div>

                    {/* State */}
                    <div>
                        <label className="lc-label">State</label>
                        <CommonSelect
                            defaultValue={
                                tempFilters.state
                                    ? { value: tempFilters.state, label: tempFilters.state }
                                    : { value: "", label: "All" }
                            }
                            options={[
                                { value: "", label: "All" },
                                ...uniqueValues.states.map((v: string) => ({
                                    value: v,
                                    label: v,
                                })),
                            ]}
                            onChange={(val: any) =>
                                setTempFilters((p: any) => ({
                                    ...p,
                                    state: val?.value ?? "",
                                }))
                            }
                        />
                    </div>

                    {/* District */}
                    <div>
                        <label className="lc-label">District</label>
                        <CommonSelect
                            defaultValue={
                                tempFilters.district
                                    ? { value: tempFilters.district, label: tempFilters.district }
                                    : { value: "", label: "All" }
                            }
                            options={[
                                { value: "", label: "All" },
                                ...uniqueValues.districts.map((v: string) => ({
                                    value: v,
                                    label: v,
                                })),
                            ]}
                            onChange={(val: any) =>
                                setTempFilters((p: any) => ({
                                    ...p,
                                    district: val?.value ?? "",
                                }))
                            }
                        />
                    </div>

                    {/* City */}
                    <div>
                        <label className="lc-label">City</label>
                        <CommonSelect
                            defaultValue={
                                tempFilters.city
                                    ? { value: tempFilters.city, label: tempFilters.city }
                                    : { value: "", label: "All" }
                            }
                            options={[
                                { value: "", label: "All" },
                                ...uniqueValues.cities.map((v: string) => ({
                                    value: v,
                                    label: v,
                                })),
                            ]}
                            onChange={(val: any) =>
                                setTempFilters((p: any) => ({
                                    ...p,
                                    city: val?.value ?? "",
                                }))
                            }
                        />
                    </div>

                    {/* Date Range */}
                    <div>
                        <label className="lc-label">Date Range</label>
                        <div className="lc-date-stack">
                            <DatePicker
                                placeholder="Start Date"
                                format="DD MMM YYYY"
                                className="lc-full-width"
                                value={tempFilters.start_date ? dayjs(tempFilters.start_date) : null}
                                onChange={(date) =>
                                    setTempFilters((p: any) => ({
                                        ...p,
                                        start_date: date ? date.format("YYYY-MM-DD") : "",
                                    }))
                                }
                            />
                            <DatePicker
                                placeholder="End Date"
                                format="DD MMM YYYY"
                                className="lc-full-width"
                                value={tempFilters.end_date ? dayjs(tempFilters.end_date) : null}
                                onChange={(date) =>
                                    setTempFilters((p: any) => ({
                                        ...p,
                                        end_date: date ? date.format("YYYY-MM-DD") : "",    // ← YYYY-MM-DD
                                    }))
                                }
                            />
                        </div>
                    </div>


                    {/* Outcomes (Used Calls Only) */}
                    {isUsedCall && (
                        <div>
                            <label className="lc-label">Call Remarks</label>
                            <CommonSelect
                                defaultValue={
                                    tempFilters.type
                                        ? { value: tempFilters.type, label: tempFilters.type }
                                        : { value: "", label: "All" }
                                }
                                options={[
                                    { value: "", label: "All" },
                                    ...REFERENCE_OPTIONS.flatMap((group) =>
                                        group.children.map((child) => ({
                                            value: child.value,
                                            label: child.label,
                                        }))
                                    ),
                                ]}
                                onChange={(val: any) =>
                                    setTempFilters((p: any) => ({ ...p, type: val?.value ?? "" }))
                                }
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Buttons */}
            <div className="lc-fields-stack">
                <button onClick={handleApplyFilters} className="lc-btn-primary">
                    Apply Filters
                </button>

                <button
                    onClick={handleReset}
                    disabled={activeFilterCount === 0}
                    className="lc-btn-secondary"
                >
                    Clear
                </button>
            </div>
        </div>
    );
};

export default LiveCallFilters;